import React, { useState, useEffect } from "react";
import {
  getRecentProjects,
  removeRecentProject,
  clearRecentProjects,
  projectExists,
  addRecentProject,
  type RecentProject,
} from "../../../services/recentProjects";
import {
  importAllData,
  validateModelData,
  clearAllModelData,
} from "../../../services/centralizedDataManager";
import { setCurrentSessionProject } from "../../../services/currentProject";
import { getDirectoryHandle } from "../../../services/fileHandleStorage";
import { useModelStore } from "../../../store/useModelStore";
import { usePropertyStore } from "../../../store/usePropertyStore";
import { useSupportStore } from "../../../store/useSupportStore";
import { useUIStore } from "../../../store/useUIStore";
import { useBeamSectionStore } from "../../../hooks/useBeamSection";
import { LoadConfirmationDialog } from "./LoadConfirmationDialog";
import { useSettingsStore } from "../../../store/useSettingsStore";
import { importFromTerminalFormat } from "../../../services/terminalFormatter";

interface RecentProjectsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  hasUnsavedChanges?: () => boolean;
  onQuickSave?: () => Promise<void>;
}

export const RecentProjectsDialog: React.FC<RecentProjectsDialogProps> = ({
  isOpen,
  onClose,
  hasUnsavedChanges,
  onQuickSave,
}) => {
  const [projects, setProjects] = useState<RecentProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadingProjectPath, setLoadingProjectPath] = useState<string | null>(
    null
  );

  const [isLoadConfirmDialogOpen, setIsLoadConfirmDialogOpen] = useState(false);
  const [pendingProject, setPendingProject] = useState<RecentProject | null>(
    null
  );

  const isElectron = typeof window !== "undefined" && window.electronAPI;

  useEffect(() => {
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen]);

  const loadProjects = async () => {
    setIsLoading(true);
    setError("");

    try {
      const allProjects = await getRecentProjects();

      if (isElectron) {
        const validProjects: RecentProject[] = [];

        for (const project of allProjects) {
          const exists = await projectExists(project.path);
          if (exists) {
            validProjects.push(project);
          } else {
            await removeRecentProject(project.path);
          }
        }

        setProjects(validProjects);
      } else {
        setProjects(allProjects);
      }
    } catch (err) {
      setError("Failed to load recent projects");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectClick = (project: RecentProject) => {
    if (hasUnsavedChanges && hasUnsavedChanges()) {
      // User has unsaved changes - show confirmation dialog
      setPendingProject(project);
      setIsLoadConfirmDialogOpen(true);
    } else {
      // No unsaved changes - load directly
      handleLoadProject(project);
    }
  };

  const handleSaveAndLoad = async () => {
    if (!pendingProject) return;

    setIsLoadConfirmDialogOpen(false);

    try {
      // Save current project first
      if (onQuickSave) {
        await onQuickSave();
      }

      // Wait a bit for save to complete
      setTimeout(() => {
        if (pendingProject) {
          handleLoadProject(pendingProject);
        }
        setPendingProject(null);
      }, 500);
    } catch (error) {
      console.error("Failed to save before load:", error);
      setError("Failed to save current project");
      setPendingProject(null);
    }
  };

  const handleDiscardAndLoad = () => {
    if (!pendingProject) return;

    setIsLoadConfirmDialogOpen(false);
    handleLoadProject(pendingProject);
    setPendingProject(null);
  };

  const handleCancelLoad = () => {
    setIsLoadConfirmDialogOpen(false);
    setPendingProject(null);
  };

  const handleLoadProject = async (project: RecentProject) => {
    setLoadingProjectPath(project.path);
    setError("");

    try {
      if (isElectron) {
        const platform = await window.electronAPI.getPlatform();
        const separator = platform === "win32" ? "\\" : "/";

        const dataPath = `${project.path}${separator}data.fema`;
        const configPath = `${project.path}${separator}config.fema`;

        const dataResult = await window.electronAPI.openProject(dataPath);
        const configResult = await window.electronAPI.openProject(configPath);

        if (!dataResult.success) {
          throw new Error(`Failed to read data.fema: ${dataResult.error}`);
        }
        if (!configResult.success) {
          throw new Error(`Failed to read config.fema: ${configResult.error}`);
        }

        // data.fema is TERMINAL FORMAT (text)
        const dataContent = dataResult.data; // Terminal format text
        const configObj = configResult.data; // JSON config

        // Clear existing data
        clearAllModelData();

        const beamSectionStore = useBeamSectionStore.getState();
        beamSectionStore.clearAllSections();

        // Import terminal format data
        const importResult = importFromTerminalFormat(dataContent);

        if (!importResult.success || importResult.errors.length > 0) {
          console.error("Import errors:", importResult.errors);
          throw new Error(`Import failed: ${importResult.errors.join(", ")}`);
        }

        // Apply settings from config (direct state update)
        if (configObj) {
          const settingsStore = useSettingsStore.getState();
          if (configObj.units) {
            settingsStore.units = configObj.units;
          }
          if (configObj.display) {
            Object.assign(settingsStore.display, configObj.display);
          }
          if (configObj.analysis) {
            Object.assign(settingsStore.analysis, configObj.analysis);
          }
          if (configObj.performance) {
            Object.assign(settingsStore.performance, configObj.performance);
          }
        }

        setCurrentSessionProject(project.path);
        useModelStore.getState().setProjectName(project.name);
        await addRecentProject(project.name, project.path);

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("predefined_loads_updated"));
          window.dispatchEvent(
            new CustomEvent("predefined_truss_presets_updated")
          );
        }, 250);

        setTimeout(() => {
          onClose();
          setLoadingProjectPath(null);
        }, 500);
      } else {
        // Browser version
        const dirHandle = await getDirectoryHandle(project.path);

        if (!dirHandle) {
          throw new Error(
            "Cannot access this project folder anymore. Please re-open it from disk and save again to refresh permissions."
          );
        }

        // Read data.fema (terminal format)
        const dataFileHandle = await dirHandle.getFileHandle("data.fema");
        const dataFile = await dataFileHandle.getFile();
        const dataContent = await dataFile.text();

        // Read config.fema (JSON)
        const configFileHandle = await dirHandle.getFileHandle("config.fema");
        const configFile = await configFileHandle.getFile();
        const configContent = await configFile.text();
        const configObj = JSON.parse(configContent);

        // Clear existing data
        clearAllModelData();

        const beamSectionStore = useBeamSectionStore.getState();
        beamSectionStore.clearAllSections();

        // Import terminal format data
        const importResult = importFromTerminalFormat(dataContent);

        if (!importResult.success || importResult.errors.length > 0) {
          console.error("Import errors:", importResult.errors);
          throw new Error(`Import failed: ${importResult.errors.join(", ")}`);
        }

        // Apply settings from config
        if (configObj) {
          const settingsStore = useSettingsStore.getState();
          if (configObj.units) {
            settingsStore.units = configObj.units;
          }
          if (configObj.display) {
            Object.assign(settingsStore.display, configObj.display);
          }
          if (configObj.analysis) {
            Object.assign(settingsStore.analysis, configObj.analysis);
          }
          if (configObj.performance) {
            Object.assign(settingsStore.performance, configObj.performance);
          }
        }

        setCurrentSessionProject(project.path);
        useModelStore.getState().setProjectName(project.name);
        await addRecentProject(project.name, project.path);

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("predefined_loads_updated"));
          window.dispatchEvent(
            new CustomEvent("predefined_truss_presets_updated")
          );
        }, 250);

        setTimeout(() => {
          onClose();
          setLoadingProjectPath(null);
        }, 500);
      }
    } catch (err: any) {
      console.error("Load error:", err);
      setError(err.message || "Failed to load project");
      setLoadingProjectPath(null);
    }
  };

  const handleRemoveProject = async (
    project: RecentProject,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();

    try {
      await removeRecentProject(project.path);
      setProjects((prev) => prev.filter((p) => p.path !== project.path));
    } catch (err) {
      setError("Failed to remove project");
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Clear all recent projects?")) return;

    try {
      await clearRecentProjects();
      setProjects([]);
    } catch (err) {
      setError("Failed to clear recent projects");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg shadow-xl w-[600px] max-w-[90vw] border border-gray-700 max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">
              Recent Projects
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="p-6 flex-1 overflow-y-auto">
            {error && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-500 rounded text-red-300 text-sm">
                ⚠️ {error}
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-400">Loading recent projects...</div>
              </div>
            ) : projects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <svg
                  className="w-16 h-16 mb-4 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                  />
                </svg>
                <p className="text-lg font-medium">No recent projects</p>
                <p className="text-sm mt-1">
                  Projects you save will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {projects.map((project) => (
                  <div
                    key={project.path}
                    className="w-full p-4 bg-gray-700 rounded border border-gray-600 hover:border-blue-500 transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <button
                        onClick={() => handleProjectClick(project)}
                        disabled={loadingProjectPath !== null}
                        className="flex-1 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <svg
                            className="w-5 h-5 text-blue-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                            />
                          </svg>
                          <h3 className="text-white font-medium truncate">
                            {project.name}
                          </h3>
                          {loadingProjectPath === project.path && (
                            <span className="text-blue-400 text-sm">
                              Loading...
                            </span>
                          )}
                        </div>
                        {isElectron && (
                          <p className="text-sm text-gray-400 truncate font-mono">
                            {project.path}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Last opened: {formatDate(project.lastOpened)}
                        </p>
                      </button>

                      <button
                        onClick={(e) => handleRemoveProject(project, e)}
                        disabled={loadingProjectPath !== null}
                        className="ml-3 p-2 text-gray-400 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                        title="Remove from recent"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center p-4 border-t border-gray-700">
            {projects.length > 0 && (
              <button
                onClick={handleClearAll}
                disabled={loadingProjectPath !== null}
                className="px-3 py-2 text-sm text-gray-400 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
              >
                Clear All
              </button>
            )}
            <div className="flex-1"></div>
            <button
              onClick={onClose}
              disabled={loadingProjectPath !== null}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Load Confirmation Dialog */}
      <LoadConfirmationDialog
        isOpen={isLoadConfirmDialogOpen}
        onCancel={handleCancelLoad}
        onDiscardAndLoad={handleDiscardAndLoad}
        onSaveAndLoad={handleSaveAndLoad}
        projectName={pendingProject?.name || ""}
      />
    </>
  );
};
